import React, { useEffect, useRef, useState } from "react";
import { DataTable, DataTablePageEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Checkbox } from "primereact/checkbox";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { Message } from "primereact/message";
import { Toast } from "primereact/toast";
import { MultiSelect } from "primereact/multiselect";
import {
  getRawMaterialPurchaseSummary,
  GetManufacrturingunitlist,
  getRawMaterialReportTotalInventory,
  getRawMaterialByPosId,
} from "../../../services/RawMaterials/RawMaterialService";
import "primeicons/primeicons.css";
import ExportRawMaterialReport from "./RawMaterial-Export";

const rupee = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

interface InventorySummary {
  posId: number;
  posCode: string;
  totalInventoryValue: number;
}

const RawMaterialReport: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [manufacturingUnits, setManufacturingUnits] = useState<any[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<number[]>([]);
  // <-- Allow null so we can send null to backend when nothing selected
  const [selectedRawMaterials, setSelectedRawMaterials] = useState<number[] | null>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [reportTime, setReportTime] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0>(1);
  const [inventorySummary, setInventorySummary] = useState<InventorySummary[]>([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);

  // ✅ Raw Material Filter
  const [rawMaterials, setRawMaterials] = useState<any[]>([]);
  const [rawMaterialLoading, setRawMaterialLoading] = useState(false);

  // ✅ Hardcoded Unit Mapping
  const getUnitLabel = (posCode: string) => {
    switch (posCode) {
      case "POS-003":
        return "Unit-1 KR Nagar";
      case "POS-004":
        return "Unit-2 Magadi Taluk";
      default:
        return posCode;
    }
  };

  // ✅ Fetch Manufacturing Units
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const result = await GetManufacrturingunitlist();
        setManufacturingUnits(Array.isArray(result) ? result : []);
      } catch (error) {
        console.error("Error fetching manufacturing units:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load manufacturing units",
        });
      }
    };
    fetchUnits();
  }, []);

  // ✅ Fetch Raw Materials when POS changes
  useEffect(() => {
    const fetchRawMaterials = async () => {
      if (!selectedUnits.length) {
        setRawMaterials([]);
        setSelectedRawMaterials([]); // keep previous behavior when no units
        return;
      }

      try {
        setRawMaterialLoading(true);
        const response = await getRawMaterialByPosId({ posIds: selectedUnits });
        setRawMaterials(response || []);
      } catch (error) {
        console.error("Error fetching raw materials:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load raw materials",
        });
      } finally {
        setRawMaterialLoading(false);
      }
    };

    fetchRawMaterials();
  }, [selectedUnits]);

  // ✅ Fetch Inventory Summary
  const fetchInventorySummary = async (posIds: number[]) => {
    if (!posIds.length) {
      setInventorySummary([]);
      return;
    }

    try {
      setInventoryLoading(true);
      const response = await getRawMaterialReportTotalInventory({ posIds });
      setInventorySummary(response.posTotals || []);
    } catch (error) {
      console.error("Error fetching inventory summary:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load inventory summary",
      });
    } finally {
      setInventoryLoading(false);
    }
  };

  const transformResponse = (data: any[]) => {
    return (data || []).map((item: any) => {
      const row: any = {
        rawMaterialName: item.rawMaterialName ?? "",
        rawMaterialID: item.rawMaterialID,
      };

      let totalQty = 0;
      
      let totalVal = 0;

      (item.posDetails || []).forEach((pd: any) => {
        if (pd.posId === 0) return;
        const posLabel = pd.posCode || `POS-${pd.posId}`;
        const qty = Number(pd.totalInvoicedQuantity ?? 0);
        const val = Number(pd.totalBasicAmount ?? 0);

        row[posLabel] = { Quantity: qty, Value: val };
        totalQty += qty;
        totalVal += val;
      });

      row.Total = { Quantity: totalQty, Value: totalVal };
      return row;
    });
  };

  const fetchReportData = async (
    page = pageNumber,
    size = pageSize,
    currentSortField = sortField,
    currentSortOrder = sortOrder
  ) => {
    if (!selectedUnits.length) {
      toast.current?.show({
        severity: "warn",
        summary: "Select Units",
        detail: "Please select at least one manufacturing unit",
      });
      return;
    }

    try {
      setLoading(true);
      const backendSortOrder =
        currentSortOrder === 1
          ? "asc"
          : currentSortOrder === -1
          ? "desc"
          : null;

      // <-- safe handling: convert selectedRawMaterials to null if not an array or empty
      const materialFilter =
        Array.isArray(selectedRawMaterials) && selectedRawMaterials.length > 0
          ? selectedRawMaterials
          : null;

      const response = await getRawMaterialPurchaseSummary({
        posIds: selectedUnits,
        rawMaterialIds: materialFilter,
        pageNumber: page,
        pageSize: size,
        sortField: currentSortField,
        sortOrder: backendSortOrder,
      });

      const transformed = transformResponse(response.data || []);
      setTableData(transformed);
      setTotalRecords(response.totalRecords ?? 0);
      setPageNumber(page);
      setPageSize(size);

      const now = new Date();
      const formatted =
        now.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }) +
        " " +
        now.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
      setReportTime(formatted);
    } catch (err) {
      console.error("Report fetch error:", err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch report data",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedUnits.length) {
      fetchReportData(1, pageSize);
      fetchInventorySummary(selectedUnits);
    } else {
      setTableData([]);
      setTotalRecords(0);
      setInventorySummary([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnits, selectedRawMaterials]);

  const handleSort = (e: any) => {
    const { sortField: newSortField, sortOrder: newSortOrder } = e;
    setSortField(newSortField);
    setSortOrder(newSortOrder);
    fetchReportData(1, pageSize, newSortField, newSortOrder);
  };

  const handlePage = (event: DataTablePageEvent) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setPageNumber(newPage);
    setPageSize(event.rows);
    fetchReportData(newPage, event.rows);
  };

  const formatNumber = (value: number) =>
    value.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  const quantityBodyTemplate = (rowData: any, field: string) =>
    formatNumber(Number(rowData[field]?.Quantity ?? 0));

  const valueBodyTemplate = (rowData: any, field: string) => (
    <span className="value-cell">₹{formatNumber(Number(rowData[field]?.Value ?? 0))}</span>
  );

  const inventoryValueBodyTemplate = (rowData: InventorySummary) => (
    <span className="value-cell">₹{formatNumber(rowData.totalInventoryValue)}</span>
  );

  const headerGroup = (
    <ColumnGroup>
      <Row>
        <Column header="Raw Material" rowSpan={2} field="rawMaterialName" sortable />
        {selectedUnits.map((posId) => {
          const unit = manufacturingUnits.find((u) => u.posID === posId);
          const unitLabel = getUnitLabel(unit?.posCode || `POS-${posId}`);
          return <Column key={`${posId}_header`} header={unitLabel} colSpan={2} />;
        })}
        <Column header="Total" colSpan={2} />
      </Row>
      <Row>
        {selectedUnits.map((posId) => {
          const unit = manufacturingUnits.find((u) => u.posID === posId);
          const posLabel = unit?.posCode || `POS-${posId}`;
          return [
            <Column header="Quantity" field={`${posLabel}_Qty`} sortable key={`qty-${posId}`} />,
            <Column header="Value" field={`${posLabel}_Val`} sortable key={`val-${posId}`} />,
          ];
        })}
        <Column header="Quantity" field="Total_Qty" sortable key="total_qty_head" />
        <Column header="Value" field="Total_Val" sortable key="total_val_head" />
      </Row>
    </ColumnGroup>
  );

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <Card title="Raw Material Inventory Value Report">
        {/* ✅ Raw Material Dropdown (Left) + Manufacturing Unit Checkboxes (Right) */}
        <div className="flex flex-wrap align-items-center gap-4 mb-4">
          {/* Manufacturing Unit Checkboxes */}
          <div className="flex flex-wrap align-items-center gap-3">
            {manufacturingUnits.map((unit) => {
              const label = getUnitLabel(unit.posCode);
              return (
                <div key={unit.posID} className="flex align-items-center gap-2">
                  <Checkbox
                    inputId={`unit-${unit.posID}`}
                    value={unit.posID}
                    onChange={(e) => {
                      const val = e.value;
                      setSelectedUnits((prev) =>
                        prev.includes(val) ? prev.filter((p) => p !== val) : [...prev, val]
                      );
                    }}
                    checked={selectedUnits.includes(unit.posID)}
                  />
                  <label htmlFor={`unit-${unit.posID}`} className="font-semibold">
                    {label}
                  </label>
                </div>
              );
            })}
          </div>

          {/* Raw Material MultiSelect */}
          <MultiSelect
            value={selectedRawMaterials ?? []} // MultiSelect expects an array; show [] when null
            options={rawMaterials.map((r) => ({
              label: r.rawMaterialName,
              value: r.rawMaterialId,
            }))}
            onChange={(e) => {
              const val = e.value as number[] | null;
              // If empty array or falsy, set to null so backend receives null (not [])
              setSelectedRawMaterials(Array.isArray(val) && val.length > 0 ? val : null);
            }}
            placeholder="Select Raw Material"
            display="chip"
            showClear
            filter
            className="w-full md:w-20rem"
            disabled={!selectedUnits.length}
            emptyFilterMessage="No raw materials"
          />
        </div>

        {/* ✅ Inventory Summary */}
        {selectedUnits.length > 0 && inventorySummary.length > 0 && (
          <div className="mb-4">
            <div className="text-900 font-bold text-lg mb-2">Total Inventory Value</div>
            <DataTable
              value={inventorySummary}
              loading={inventoryLoading}
              showGridlines
              className="p-datatable-sm"
              style={{ width: "400px" }}
            >
              <Column
                field="posCode"
                header="Unit"
                body={(row) => getUnitLabel(row.posCode)}
                style={{ minWidth: "150px", fontWeight: "bold" }}
              />
              <Column
                field="totalInventoryValue"
                header="Total Inventory Value"
                body={inventoryValueBodyTemplate}
                style={{ minWidth: "200px", textAlign: "right" }}
              />
            </DataTable>
          </div>
        )}

      {selectedUnits.length > 0 && (
  <div className="flex justify-content-between align-items-start mb-3 gap-2 flex-wrap">
    <div className="flex flex-column gap-2">
      <Message
        severity="info"
        text={`The material value is calculated using the average basic amount from the last three purchases based on invoiced quantities.`}
      />
      <Message
        severity="info"
        text={`This report was generated on ${reportTime}. The values displayed here  do not include any applicable taxes.`}

      />
    </div>

    <ExportRawMaterialReport
      selectedUnits={selectedUnits}
      selectedRawMaterials={selectedRawMaterials}
      disabled={loading}
    />
  </div>
)}


        <Divider />

        {/* ✅ Main DataTable */}
        {selectedUnits.length > 0 ? (
          <DataTable
            value={tableData}
            loading={loading}
            headerColumnGroup={headerGroup}
            showGridlines
            className="p-datatable-sm"
            paginator
            lazy
            rows={pageSize}
            first={(pageNumber - 1) * pageSize}
            totalRecords={totalRecords}
            rowsPerPageOptions={[10, 25, 50, 100]}
            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSort}
            onPage={handlePage}
            stripedRows
            emptyMessage="No records found."
            responsiveLayout="scroll"
          >
            <Column
              field="rawMaterialName"
              header="Raw Material"
              sortable
              sortField="rawmaterialname"
              style={{ minWidth: "200px" }}
            />
            {selectedUnits.map((posId) => {
              const unit = manufacturingUnits.find((u) => u.posID === posId);
              const posLabel = unit?.posCode || `POS-${posId}`;
              return [
                <Column
                  key={`${posLabel}_qty`}
                  body={(rowData) => quantityBodyTemplate(rowData, posLabel)}
                  sortable
                  style={{ textAlign: "center", minWidth: "100px" }}
                />,
                <Column
                  key={`${posLabel}_val`}
                  body={(rowData) => valueBodyTemplate(rowData, posLabel)}
                  sortable
                  style={{ textAlign: "center", minWidth: "120px" }}
                />,
              ];
            })}
            <Column
              key="total_qty"
              body={(rowData) => quantityBodyTemplate(rowData, "Total")}
              sortable
              style={{ textAlign: "center", fontWeight: "bold", minWidth: "100px" }}
            />
            <Column
              key="total_val"
              body={(rowData) => valueBodyTemplate(rowData, "Total")}
              sortable
              style={{ textAlign: "center", fontWeight: "bold", minWidth: "120px" }}
            />
          </DataTable>
        ) : (
          <div className="p-4 text-center">
            <i className="pi pi-info-circle mr-2" />
            Please select at least one manufacturing unit to view the report
          </div>
        )}
      </Card>
    </div>
  );
};

export default RawMaterialReport;
